import React, { useContext } from 'react';
import Button from '@mui/material/Button';
import { ThemeContext } from 'styled-components';

/**
 * Single button that toggles between "cancel" and "primary" (brand) state.
 * - When isPrimaryActive is false: shows cancelLabel, cancel styling, onCancelClick.
 * - When isPrimaryActive is true: shows primaryContent, brand styling, onPrimaryClick.
 * Brand-enabled styling is fully controlled here (brandSecondary, white text, etc.).
 */
const DynamicActionButton = ({
    isPrimaryActive,
    cancelLabel = 'Cancel',
    primaryContent,
    onCancelClick,
    onPrimaryClick,
    disabled = false,
    fullWidth = true,
    size = 'large',
    ...buttonProps
}) => {
    const theme = useContext(ThemeContext);

    const isBrandEnabled = isPrimaryActive && !disabled;

    const handleClick = () => {
        if (isBrandEnabled) {
            onPrimaryClick?.();
        } else {
            onCancelClick?.();
        }
    };

    const cancelSx = {
        textTransform: 'none',
        borderRadius: '8px',
        border: `1px solid ${theme?.colors?.border || '#ccc'}`,
        backgroundColor: 'transparent',
        color: theme?.colors?.text,
        '&:hover': {
            borderColor: theme?.colors?.border || '#ccc',
            backgroundColor: theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)',
        },
    };

    const brandEnabledSx = {
        textTransform: 'none',
        borderRadius: '8px',
        borderColor: theme?.current === 'light'
            ? theme?.colors?.brandSecondary
            : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
        color: '#ffffff',
        backgroundColor: theme?.current === 'light'
            ? theme?.colors?.brandSecondary
            : (theme?.colors?.tableBackground || theme?.colors?.background),
        '&:hover': {
            borderColor: theme?.current === 'light'
                ? theme?.colors?.brandSecondary
                : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
            backgroundColor: theme?.current === 'light'
                ? theme?.colors?.brandSecondary
                : (theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)'),
        },
    };

    return (
        <Button
            variant="outlined"
            fullWidth={fullWidth}
            size={size}
            disabled={disabled}
            onClick={handleClick}
            sx={isBrandEnabled ? brandEnabledSx : cancelSx}
            {...buttonProps}
        >
            {isBrandEnabled ? primaryContent : cancelLabel}
        </Button>
    );
};

export default DynamicActionButton;
